/**
 * LANE DNA-BUILD (Phase 2) — pure layout geometry for the career through-line
 * spine (CareerThroughLine.tsx). Layout-logic-VERBATIM port of apps/web/src/
 * components/viz/CareerThroughLine.tsx's inline stationX/stationY/
 * throughLinePath functions — extracted into its own module so the geometry
 * is unit-testable without a DOM/SVG harness, mirroring radarPath.ts's exact
 * placement precedent ("pure path math... extracted so the geometry is
 * unit-testable without a DOM/SVG harness — see this app's house idiom").
 *
 * NO INVENTION: this module never decides how many eras exist or what their
 * labels/gold/origin are — it only lays out whatever era list the caller
 * hands it. Geometry only; CareerThroughLine.tsx still owns every data
 * decision (what's gold, what's captured-vs-imported, what the readiness
 * score is).
 */

export interface ThroughLinePoint {
  x: number;
  y: number;
}

// ── Layout constants (SVG user units; the SVG scales to its container) ────────
export const VIEW_W = 1000;
export const PAD_X = 64; // horizontal breathing room before the first / after the last station
export const BASELINE_Y = 300; // the spine's y

/** Evenly space N stations across the usable width. One station sits centred. */
export function stationX(index: number, count: number): number {
  if (count <= 1) return VIEW_W / 2;
  const usable = VIEW_W - PAD_X * 2;
  return PAD_X + (usable * index) / (count - 1);
}

/** Give each station a gentle crest/dip so the through-line reads as a weave, not
 *  a straight line. Deterministic from the index (no animation jitter). */
export function stationY(index: number, count: number): number {
  if (count <= 1) return BASELINE_Y;
  // a calm sine ripple, amplitude ±22px, biased so the curve rises toward "now".
  const t = index / Math.max(1, count - 1);
  const ripple = Math.sin(t * Math.PI * 1.5) * 18;
  const lift = t * 10; // the present sits a touch higher (forward momentum)
  return BASELINE_Y - ripple - lift;
}

/** A smooth cubic path through the station points (Catmull-Rom → Bézier). This is
 *  the visual "through-line": it weaves THROUGH every station, dipping/cresting so
 *  the eye follows the continuity, not a flat rule. Pure geometry. */
export function throughLinePath(points: ThroughLinePoint[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const { y } = points[0];
    return `M ${PAD_X} ${y} L ${VIEW_W - PAD_X} ${y}`;
  }
  const k = 0.5; // tension
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1.x + ((p2.x - p0.x) / 6) * k * 2;
    const c1y = p1.y + ((p2.y - p0.y) / 6) * k * 2;
    const c2x = p2.x - ((p3.x - p1.x) / 6) * k * 2;
    const c2y = p2.y - ((p3.y - p1.y) / 6) * k * 2;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}
